// Плановые отчёты — admin-only API (ANX-068).
import type { ApiClient } from './client';

export type ReportFrequency = 'daily' | 'weekly' | 'monthly';

export interface ReportSchedule {
  enabled: boolean;
  frequency: ReportFrequency;
  recipients: string[];
  timezone: string;
  lastRunAt?: string;
  nextRunAt?: string;
  updatedAt?: string;
}

interface RawSchedule {
  enabled: boolean;
  frequency: ReportFrequency;
  recipients: string[];
  timezone: string;
  last_run_at?: string;
  next_run_at?: string;
  updated_at?: string;
}

function toSchedule(r: RawSchedule): ReportSchedule {
  const s: ReportSchedule = {
    enabled: r.enabled,
    frequency: r.frequency,
    recipients: r.recipients ?? [],
    timezone: r.timezone || 'UTC',
  };
  if (r.last_run_at) s.lastRunAt = r.last_run_at;
  if (r.next_run_at) s.nextRunAt = r.next_run_at;
  if (r.updated_at) s.updatedAt = r.updated_at;
  return s;
}

export async function getReportSchedule(client: ApiClient): Promise<ReportSchedule> {
  const raw = await client.request<RawSchedule>('/api/v1/reports/schedule');
  return toSchedule(raw);
}

export async function putReportSchedule(
  client: ApiClient,
  input: { enabled: boolean; frequency: ReportFrequency; recipients: string[]; timezone: string },
): Promise<ReportSchedule> {
  const raw = await client.request<RawSchedule>('/api/v1/reports/schedule', {
    method: 'PUT',
    json: {
      enabled: input.enabled,
      frequency: input.frequency,
      recipients: input.recipients,
      timezone: input.timezone,
    },
  });
  return toSchedule(raw);
}

// ---- Архив отчётов --------------------------------------------------------

export interface ReportRunSummary {
  id: string;
  frequency: ReportFrequency;
  periodStart: string; // YYYY-MM-DD
  periodEnd: string;
  recipientsCount: number;
  emailed: boolean;
  createdAt: string;
}

interface RawRun {
  id: string;
  frequency: ReportFrequency;
  period_start: string;
  period_end: string;
  recipients_count: number;
  emailed: boolean;
  created_at: string;
}

function toRun(r: RawRun): ReportRunSummary {
  return {
    id: r.id,
    frequency: r.frequency,
    periodStart: r.period_start,
    periodEnd: r.period_end,
    recipientsCount: r.recipients_count,
    emailed: r.emailed,
    createdAt: r.created_at,
  };
}

export async function listReportRuns(client: ApiClient): Promise<ReportRunSummary[]> {
  const raw = await client.request<{ runs: RawRun[] }>('/api/v1/reports/runs');
  return (raw.runs ?? []).map(toRun);
}

// ReportMetrics — зеркало reports.Summary (backend JSONB). null = метрика
// неприменима (нет данных / деление на ноль).
export interface ReportMetrics {
  frequency: ReportFrequency;
  period_start: string;
  period_end: string;
  total_chats: number;
  prev_total_chats: number;
  chats_delta_pct: number | null;
  first_response_p50_secs: number | null;
  avg_response_secs: number | null;
  csat_positive: number;
  csat_negative: number;
  csat_score_pct: number | null;
  prev_csat_score_pct: number | null;
  operators: { name: string; chats: number; csat_positive: number; csat_negative: number }[];
  top_tags: { name: string; color: string; count: number }[];
  busiest: { weekday: number; hour: number; count: number; label: string } | null;
}

export async function getReportRun(
  client: ApiClient,
  id: string,
): Promise<{ run: ReportRunSummary; metrics: ReportMetrics }> {
  const raw = await client.request<{ run: RawRun; metrics: ReportMetrics }>(
    `/api/v1/reports/runs/${id}`,
  );
  return { run: toRun(raw.run), metrics: raw.metrics };
}
